import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Avatar,
  Box,
  ButtonBase,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { lazy, memo, Suspense, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth';
import { useAppShell } from '@/shell';
import { formatRoleLabel, getUserInitials } from '@/utils/userDisplayName';
import { NotificationButton } from './NotificationButton';

const UserProfileMenu = lazy(async () => {
  const module = await import('@/components/profile/UserProfileMenu');
  return { default: module.UserProfileMenu };
});

export const HeaderActions = memo(function HeaderActions() {
  const { t } = useTranslation('entity-portal');
  const { user } = useAuth();
  const {
    userProfile,
    isUserProfileLoading,
    profileMenuAnchorEl,
    isProfileMenuOpen,
    openProfileMenu,
    closeProfileMenu,
  } = useAppShell();

  const displayName =
    user?.displayName ??
    userProfile?.displayName ??
    t('profile.profileNameFallback');
  const roleLabel = userProfile?.roles?.length
    ? formatRoleLabel(userProfile.roles[0])
    : t('appHeader.appHeaderDefaultRole');

  const handleOpenProfile = (event: MouseEvent<HTMLElement>) => {
    openProfileMenu(event.currentTarget);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <NotificationButton />

        <ButtonBase
          aria-label={t('profile.profileMenuOpen')}
          aria-controls={isProfileMenuOpen ? 'user-profile-hub' : undefined}
          aria-haspopup="true"
          aria-expanded={isProfileMenuOpen ? 'true' : undefined}
          onClick={handleOpenProfile}
          sx={{
            borderRadius: 2,
            px: 0.75,
            py: 0.5,
            ml: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          {isUserProfileLoading ? (
            <Skeleton variant="circular" width={32} height={32} />
          ) : (
            <Avatar
              src={userProfile?.avatar ?? user?.photoUrl}
              alt={displayName}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                typography: 'subtitle2',
              }}
            >
              {displayName !== t('profile.profileNameFallback') ? (
                getUserInitials(displayName)
              ) : (
                <AccountCircleIcon fontSize="small" />
              )}
            </Avatar>
          )}

          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
              textAlign: 'left',
              minWidth: 0,
            }}
          >
            {isUserProfileLoading ? (
              <>
                <Skeleton width={84} height={20} />
                <Skeleton width={60} height={16} />
              </>
            ) : (
              <>
                <Typography variant="subtitle1" noWrap>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {roleLabel}
                </Typography>
              </>
            )}
          </Box>

          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              color: 'text.secondary',
              display: { xs: 'none', md: 'block' },
            }}
          />
        </ButtonBase>
      </Stack>

      <Suspense fallback={null}>
        <UserProfileMenu
          anchorEl={profileMenuAnchorEl}
          open={isProfileMenuOpen}
          onClose={closeProfileMenu}
        />
      </Suspense>
    </>
  );
});
